import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertDashboardWidget } from '@/components/alerts/AlertDashboardWidget'
import { RealTimeMetrics } from '@/components/dashboard/RealTimeMetrics'
import { FinancialImpactGrid } from '@/components/dashboard/FinancialImpactGrid'
import { RecentLeadsTable } from '@/components/dashboard/RecentLeadsTable'
import { ActivePatternsCard } from '@/components/dashboard/ActivePatternsCard'
import { QuickActionsCard } from '@/components/dashboard/QuickActionsCard'
import { FraudPatternsOverview } from '@/components/dashboard/FraudPatternsOverview'
import { SequentialPhonePattern } from '@/components/dashboard/SequentialPhonePattern'
import { IPPatternDashboard } from '@/components/dashboard/IPPatternDashboard'
import { VelocityAttackMonitor } from '@/components/dashboard/VelocityAttackMonitor'
import { GeographicConcentration } from '@/components/dashboard/GeographicConcentration'
import { PatternCorrelation } from '@/components/dashboard/PatternCorrelation'
import { LivePatternFeed } from '@/components/dashboard/LivePatternFeed'
import { LoadingState } from '@/components/ui/Feedback'
import { MaintenanceMode } from '@/components/shared/MaintenanceMode'
import { DebugPanel } from '@/components/dev/DebugPanel'
import { generateMockDashboardData, generateMockLead, type DashboardData, type Lead } from '@/services/mockData'
import { generateMockLeads, type LeadTableRow } from '@/services/mockLeads'
import { FeatureFlags, config } from '@/config'
import { logger } from '@/utils/logger'
import { MobileDashboard } from '@/components/mobile/MobileDashboard'
import { useMobileResponsive } from '@/hooks/useMobileResponsive'

export function Dashboard() {
  const [realtimeLeads, setRealtimeLeads] = useState<Lead[]>([])
  const [leadTableRows, setLeadTableRows] = useState<LeadTableRow[]>([])
  const { isMobile } = useMobileResponsive()

  // Log dashboard access
  useEffect(() => {
    logger.userAction('dashboard_accessed', {
      environment: config.environment,
      mockMode: config.mockMode,
      features: FeatureFlags.getAll()
    })
  }, [])

  // Generate lead table data
  useEffect(() => {
    const mockLeads = generateMockLeads(20)
    setLeadTableRows(mockLeads)
  }, [])

  // Fetch initial dashboard data
  const { data, isLoading, refetch } = useQuery<DashboardData>({
    queryKey: ['dashboard'],
    queryFn: generateMockDashboardData,
    refetchInterval: FeatureFlags.isEnabled('enableRealTimeAlerts') ? 30000 : 60000,
    enabled: !FeatureFlags.isInMaintenanceMode()
  })

  // Simulate real-time lead updates
  useEffect(() => {
    if (!FeatureFlags.isEnabled('enableRealTimeAlerts') || FeatureFlags.isInMaintenanceMode()) {
      return
    }
    
    const interval = setInterval(() => {
      const newLead = generateMockLead()
      setRealtimeLeads(prev => [newLead, ...prev.slice(0, 49)]) // Keep latest 50 leads
      
      // Log new lead
      logger.info('New lead generated', { 
        leadId: newLead.id, 
        riskScore: newLead.riskScore,
        flags: newLead.flags 
      })
      
      // Log fraud detection for high-risk leads
      if (newLead.riskScore >= 80) {
        logger.fraudDetected(newLead.id, newLead.riskScore, newLead.flags)
      }
    }, 5000) // New lead every 5 seconds

    return () => clearInterval(interval)
  }, [])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto">
          <LoadingState type="skeleton" rows={8} />
        </div>
      </div>
    )
  }

  const combinedLeads = realtimeLeads.length > 0 ? realtimeLeads : data?.recentLeads || []

  const handleApprove = (leadId: string) => {
    setLeadTableRows(prev => 
      prev.map(lead => 
        lead.id === leadId ? { ...lead, status: 'approved' as const } : lead
      )
    )
  }

  const handleReject = (leadId: string) => {
    setLeadTableRows(prev => 
      prev.map(lead => 
        lead.id === leadId ? { ...lead, status: 'rejected' as const } : lead
      )
    )
  }

  const handleViewDetails = (leadId: string) => {
    console.log('View details for lead:', leadId)
  }

  // Mobile Dashboard
  if (isMobile) {
    return (
      <MobileDashboard
        leads={leadTableRows}
        onApprove={handleApprove}
        onReject={handleReject}
        onViewDetails={handleViewDetails}
      />
    )
  }

  return (
    <div className="w-full max-w-full space-y-6 overflow-hidden">
        {/* Dashboard Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Fraud Detection Dashboard
              </h1>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>Real-time monitoring</span>
                {FeatureFlags.isEnabled('enableRealTimeAlerts') && (
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span>Live</span>
                  </div>
                )}
              </div>
            </div>
            
            {config.environment !== 'production' && (
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-1 bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 rounded">
                  {config.environment.toUpperCase()}
                </span>
                {config.mockMode && (
                  <span className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 rounded">
                    MOCK
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Real-Time Metrics */}
        <RealTimeMetrics />
        
        {/* Key Business Metrics */}
        <FinancialImpactGrid stats={data?.todayStats} />
        
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 mb-8">
          {/* Lead Review Table - Takes more space on large screens */}
          <div className="xl:col-span-8 overflow-x-auto">
            <RecentLeadsTable leads={combinedLeads} />
          </div>
          
          {/* Pattern Detection Summary - Stack on smaller screens */}
          <div className="xl:col-span-4 space-y-6">
            <ActivePatternsCard patterns={data?.activePatterns || []} />
            <AlertDashboardWidget compact={true} maxAlerts={3} />
            {FeatureFlags.canUseTeamFeatures() && <QuickActionsCard />}
          </div>
        </div>
        
        {/* Pattern Detection Cards */}
        {FeatureFlags.canUseAdvancedAnalytics() && (
          <div className="space-y-6">
            <FraudPatternsOverview patterns={data?.activePatterns || []} />
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <div className="sm:col-span-2 lg:col-span-1 xl:col-span-2">
                <SequentialPhonePattern />
              </div>
              <div className="sm:col-span-2 lg:col-span-2 xl:col-span-2">
                <VelocityAttackMonitor />
              </div>
            </div>
            
            <IPPatternDashboard />
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <GeographicConcentration />
              <LivePatternFeed />
            </div>
            
            <PatternCorrelation />
          </div>
        )}
        
        {/* Bottom Stats Bar */}
        <div className="mt-8 bg-card border rounded-lg p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-foreground">79</p>
              <p className="text-sm text-muted-foreground">Total Blocked Today</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">3</p>
              <p className="text-sm text-muted-foreground">Processing Queue</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">47ms</p>
              <p className="text-sm text-muted-foreground">Avg Detection Time</p>
            </div>
            <div className="flex items-center justify-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm text-foreground font-medium">System Operational</span>
            </div>
          </div>
        </div>
    </div>
  )
}